import type { AdminClientContext, AdminHttpClient } from './client.js';

type MethodKeys =
  | 'setTenantSelfSignup'
  | 'getDefaultSignupTenant'
  | 'setDefaultSignupTenant'
  | 'assignTenantCapabilitySource'
  | 'revokeTenantCapabilitySource'
  | 'getSocialConfig'
  | 'updateSocialMode'
  | 'createSocialProvider'
  | 'updateSocialProvider'
  | 'deleteSocialProvider'
  | 'getOrganizationSsoConfig'
  | 'updateOrganizationSsoMode'
  | 'createOrganizationSsoConnection'
  | 'updateOrganizationSsoConnection'
  | 'deleteOrganizationSsoConnection'
  | 'testOrganizationSsoConnection'
  | 'setOrganizationSsoConnectionEnabled'
  | 'getOrganizationSsoSamlMetadata'
  | 'getAutoRoutingProfile'
  | 'getRealtimeVoice'
  | 'putRealtimeVoice'
  | 'getVoiceSettings'
  | 'putVoiceSettings'
  | 'getTenantVoice'
  | 'putTenantVoice'
  | 'postTenantVoiceModel'
  | 'getRecommendedModelProfile'
  | 'putRecommendedModelProfile'
  | 'putAutoRoutingProfile'
  | 'previewAutoRouting'
  | 'listAutoRoutingDecisions'
  | 'bootstrap'
  | 'overview'
  | 'listModelAccess'
  | 'listCatalog'
  | 'listModelRoutes'
  | 'listQuotas'
  | 'getProviderKeys'
  | 'listPlatformProviderModels'
  | 'createPlatformProviderModel'
  | 'updatePlatformProviderModel'
  | 'testPlatformProviderModel'
  | 'deployPlatformProviderModel'
  | 'bulkDeployPlatformProviderModels'
  | 'publishPlatformProviderModelRates'
  | 'revokePlatformProviderModelDeployment'
  | 'disablePlatformProviderModel'
  | 'listWebSearchBackends'
  | 'listMediaRoutes'
  | 'getPlatformWebSearchKey'
  | 'setPlatformWebSearchKey'
  | 'getCurrentRates'
  | 'getRateSettings'
  | 'putRateSettings'
  | 'adoptPublishedRateSettings'
  | 'testProviderKey'
  | 'testLiteLLMConnection'
  | 'getTenantCatalog'
  | 'getTenantAssignment'
  | 'createModelRoute'
  | 'removeCustomProviderModel'
  | 'previewPolicy'
  | 'registerProviderKey'
  | 'removeProviderKey'
  | 'setTenantCustomerModelLabels'
  | 'setWebSearchBackend'
  | 'setMediaRoute'
  | 'setTenantAssignment'
  | 'platformDiagnosticBundles'
  | 'platformDiagnosticBundleDownload';

export function buildPlatformMethods(ctx: AdminClientContext) {
  const { transport, token, idemKey } = ctx;

  return {
    setTenantSelfSignup: (tenantId, patch, idempotencyKey) =>
      transport.setTenantSelfSignup(token(), {
        tenantId,
        ...patch,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    getDefaultSignupTenant: () => transport.getDefaultSignupTenant(token()),
    setDefaultSignupTenant: (tenantId, idempotencyKey) =>
      transport.setDefaultSignupTenant(token(), {
        tenantId,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    assignTenantCapabilitySource: (tenantId, sourceId, idempotencyKey) =>
      transport.assignTenantCapabilitySource(token(), {
        tenantId,
        sourceId,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    revokeTenantCapabilitySource: async (tenantId, sourceId, idempotencyKey) => {
      await transport.revokeTenantCapabilitySource(token(), {
        tenantId,
        sourceId,
        idempotencyKey: idempotencyKey ?? idemKey(),
      });
    },
    getAutoRoutingProfile: (tenantId) =>
      transport.getAutoRoutingProfile(token(), tenantId),
    getRealtimeVoice: (tenantId) => transport.getRealtimeVoice(token(), tenantId),
    putRealtimeVoice: (tenantId, input, idempotencyKey) => transport.putRealtimeVoice(token(), tenantId, input, idempotencyKey ?? idemKey()),
    getVoiceSettings: () => transport.getVoiceSettings(token()),
    putVoiceSettings: (input, idempotencyKey) => transport.putVoiceSettings(token(), input, idempotencyKey ?? idemKey()),
    getTenantVoice: (tenantId) => transport.getTenantVoice(token(), tenantId),
    putTenantVoice: (tenantId, input, idempotencyKey) => transport.putTenantVoice(token(), tenantId, input, idempotencyKey ?? idemKey()),
    postTenantVoiceModel: (tenantId, input, idempotencyKey) => transport.postTenantVoiceModel(token(), tenantId, input, idempotencyKey ?? idemKey()),
    getRecommendedModelProfile: (tenantId) => transport.getRecommendedModelProfile(token(), tenantId),
    putRecommendedModelProfile: (tenantId, profile, expectedRevision, idempotencyKey) =>
      transport.putRecommendedModelProfile(token(), { tenantId, profile, expectedRevision, idempotencyKey: idempotencyKey ?? idemKey() }),
    putAutoRoutingProfile: (tenantId, profile, idempotencyKey) =>
      transport.putAutoRoutingProfile(token(), {
        tenantId,
        profile,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    previewAutoRouting: (tenantId, preview) =>
      transport.previewAutoRouting(token(), { tenantId, preview }),
    listAutoRoutingDecisions: (tenantId, limit) =>
      transport.listAutoRoutingDecisions(token(), tenantId, limit),
    platformDiagnosticBundles: (query) =>
      transport.platformDiagnosticBundles(token(), query),
    platformDiagnosticBundleDownload: (id) =>
      transport.platformDiagnosticBundleDownload(token(), id),
    getSocialConfig: (tenantId) => transport.getSocialConfig(token(), tenantId),
    updateSocialMode: (tenantId, socialLoginMode, idempotencyKey) =>
      transport.updateSocialMode(token(), {
        tenantId,
        socialLoginMode,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    createSocialProvider: (tenantId, input, idempotencyKey) =>
      transport.createSocialProvider(token(), {
        tenantId,
        ...input,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    updateSocialProvider: (tenantId, providerId, patch, idempotencyKey) =>
      transport.updateSocialProvider(token(), {
        tenantId,
        providerId,
        ...patch,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    deleteSocialProvider: async (tenantId, providerId, idempotencyKey) => {
      await transport.deleteSocialProvider(token(), {
        tenantId,
        providerId,
        idempotencyKey: idempotencyKey ?? idemKey(),
      });
    },
    getOrganizationSsoConfig: (tenantId) =>
      transport.getOrganizationSsoConfig(token(), tenantId),
    updateOrganizationSsoMode: (tenantId, mode, confirmBreakGlass, idempotencyKey) =>
      transport.updateOrganizationSsoMode(token(), {
        tenantId,
        mode,
        ...(confirmBreakGlass === undefined ? {} : { confirmBreakGlass }),
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    createOrganizationSsoConnection: (tenantId, connection, idempotencyKey) =>
      transport.createOrganizationSsoConnection(token(), {
        tenantId,
        connection,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    updateOrganizationSsoConnection: (tenantId, connectionId, patch, idempotencyKey) =>
      transport.updateOrganizationSsoConnection(token(), {
        tenantId,
        connectionId,
        patch,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    deleteOrganizationSsoConnection: async (tenantId, connectionId, idempotencyKey) => {
      await transport.deleteOrganizationSsoConnection(token(), {
        tenantId,
        connectionId,
        idempotencyKey: idempotencyKey ?? idemKey(),
      });
    },
    testOrganizationSsoConnection: (tenantId, connectionId, test, idempotencyKey) =>
      transport.testOrganizationSsoConnection(token(), {
        tenantId,
        connectionId,
        ...(test === undefined ? {} : { test }),
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    setOrganizationSsoConnectionEnabled: (tenantId, connectionId, enabled, idempotencyKey) =>
      transport.setOrganizationSsoConnectionEnabled(token(), {
        tenantId,
        connectionId,
        enabled,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    getOrganizationSsoSamlMetadata: (tenantId, connectionId) =>
      transport.getOrganizationSsoSamlMetadata(token(), tenantId, connectionId),
    bootstrap: () => transport.bootstrap(token()),
    overview: () => transport.overview(token()),
    listModelAccess: () => transport.listModelAccess(token()),
    listCatalog: () => transport.catalog(token()),
    listModelRoutes: () => transport.listModelRoutes(token()),
    listQuotas: () => transport.listQuotas(token()),
    getProviderKeys: (tenantId) => transport.getProviderKeys(token(), tenantId),
    listPlatformProviderModels: (query) =>
      transport.listPlatformProviderModels(token(), query),
    createPlatformProviderModel: (input, idempotencyKey) =>
      transport.createPlatformProviderModel(token(), {
        ...input,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    updatePlatformProviderModel: (provider, modelKey, input, idempotencyKey) =>
      transport.updatePlatformProviderModel(token(), provider, modelKey, {
        ...input,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    testPlatformProviderModel: (provider, modelKey, tenantId) =>
      transport.testPlatformProviderModel(token(), provider, modelKey, tenantId),
    deployPlatformProviderModel: (provider, modelKey, input, idempotencyKey) =>
      transport.deployPlatformProviderModel(token(), provider, modelKey, {
        ...input,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    bulkDeployPlatformProviderModels: (tenantId, input, idempotencyKey) =>
      transport.bulkDeployPlatformProviderModels(token(), tenantId, {
        ...input,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    publishPlatformProviderModelRates: (provider, modelKey, input, idempotencyKey) =>
      transport.publishPlatformProviderModelRates(token(), provider, modelKey, {
        ...input,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    revokePlatformProviderModelDeployment: (
      provider,
      modelKey,
      tenantId,
      idempotencyKey
    ) =>
      transport.revokePlatformProviderModelDeployment(
        token(),
        provider,
        modelKey,
        tenantId,
        idempotencyKey ?? idemKey()
      ),
    disablePlatformProviderModel: (provider, modelKey, idempotencyKey) =>
      transport.disablePlatformProviderModel(token(), provider, modelKey, idempotencyKey ?? idemKey()),
    listWebSearchBackends: (tenantId) => transport.listTenantWebSearchBackends(token(), tenantId),
    listMediaRoutes: (tenantId) => transport.listTenantMediaRoutes(token(), tenantId),
    getPlatformWebSearchKey: () => transport.getPlatformWebSearchKey(token()),
    setPlatformWebSearchKey: (input, idempotencyKey) =>
      transport.setPlatformWebSearchKey(token(), {
        ...input,
        idempotencyKey: idempotencyKey ?? idemKey()
      }),
    getCurrentRates: () => transport.getCurrentRates(token()),
    getRateSettings: () => transport.getRateSettings(token()),
    putRateSettings: (input, idempotencyKey) =>
      transport.putRateSettings(token(), {
        ...input,
        idempotencyKey: idempotencyKey ?? idemKey()
      }),
    adoptPublishedRateSettings: (idempotencyKey) =>
      transport.adoptPublishedRateSettings(token(), idempotencyKey ?? idemKey()),
    testProviderKey: (input) => transport.testProviderKey(token(), input),
    testLiteLLMConnection: (input) => transport.testLiteLLMConnection(token(), input),
    getTenantCatalog: (tenantId) => transport.getTenantCatalog(token(), tenantId),
    getTenantAssignment: (tenantId) => transport.getTenantAssignment(token(), tenantId),
    createModelRoute: async (route, catalogItem, idempotencyKey) => {
      await transport.createModelRoute(token(), {
        route,
        catalogItem,
        idempotencyKey: idempotencyKey ?? idemKey()
      });
      return {
        id: route.id,
        tenantId: route.tenantId,
        catalogModelId: route.catalogModelId,
        routeKind: route.routeKind,
        providerProtocol: route.providerProtocol,
        managedBy: route.managedBy,
        status: route.status,
        hasEndpoint: Boolean(route.endpointRef),
        hasCredential: Boolean(route.credentialRef)
      };
    },
    removeCustomProviderModel: (catalogModelId, idempotencyKey) =>
      transport.removeCustomProviderModel(token(), {
        catalogModelId,
        idempotencyKey: idempotencyKey ?? idemKey()
      }),
    previewPolicy: (draft) => transport.previewPolicy(token(), draft),
    registerProviderKey: (input, idempotencyKey) =>
      transport.registerProviderKey(token(), {
        ...input,
        idempotencyKey: idempotencyKey ?? idemKey()
      }),
    removeProviderKey: (tenantId, provider, idempotencyKey) =>
      transport.removeProviderKey(token(), {
        tenantId,
        provider,
        idempotencyKey: idempotencyKey ?? idemKey()
      }),
    setTenantCustomerModelLabels: (tenantId, labels, idempotencyKey) =>
      transport.setTenantCustomerModelLabels(token(), {
        tenantId,
        labels,
        idempotencyKey: idempotencyKey ?? idemKey()
      }),
    setWebSearchBackend: (input, idempotencyKey) =>
      transport.setTenantWebSearchBackend(token(), {
        ...input,
        idempotencyKey: idempotencyKey ?? idemKey()
      }),
    setMediaRoute: (input, idempotencyKey) =>
      transport.setTenantMediaRoute(token(), {
        ...input,
        idempotencyKey: idempotencyKey ?? idemKey()
      }),
    setTenantAssignment: (
      tenantId,
      allowedModelIds,
      defaultMemberModelIds,
      freeTierModelIds,
      idempotencyKey
    ) =>
      transport.setTenantAssignment(token(), {
        tenantId,
        allowedModelIds,
        defaultMemberModelIds,
        ...(freeTierModelIds === undefined ? {} : { freeTierModelIds }),
        idempotencyKey: idempotencyKey ?? idemKey()
      })
  } satisfies Pick<AdminHttpClient, MethodKeys>;
}
